import { useRef, useState, useEffect } from 'react';
import vitaIcon from './assets/vita-icon.png';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from './firebase';

import { useAuth } from './AuthContext';
import { useVitaEngine } from './hooks/useVitaEngine';
import { useCognitiveAlerts } from './hooks/useCognitiveAlerts';
import { buildProjectPrediction } from './utils/buildPredictiveEngine';

import Dashboard from './components/Dashboard';
import MapSection from './components/MapSection';
import KnowledgeGraph from './components/KnowledgeGraph';
import CognitiveAlerts from './components/CognitiveAlerts';
import GlobalSearch from './components/GlobalSearch';
import AnalyticsPanel from './components/AnalyticsPanel';
import TerritorialPanel from './components/TerritorialPanel';
import ExecutiveReport from './components/ExecutiveReport';

import { FiMap, FiSearch, FiBarChart2, FiLogOut, FiShield } from 'react-icons/fi';

function WelcomeScreen({ onDismiss }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div onClick={onDismiss} style={{ position: 'fixed', inset: 0, background: 'radial-gradient(circle at 30% 20%, rgba(31,169,122,0.15), transparent 50%), radial-gradient(circle at 70% 80%, rgba(9,147,181,0.15), transparent 50%), #071a2f', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'pointer', animation: 'fadeIn 0.5s ease' }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
        @keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
      `}</style>
      <img src={vitaIcon} alt="VITA" style={{ width: '120px', height: '120px', objectFit: 'contain', marginBottom: '32px', filter: 'drop-shadow(0 0 32px rgba(0,200,160,0.3))' }} />
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '52px', fontWeight: '700', color: 'white', letterSpacing: '0.08em', marginBottom: '8px' }}>VITA</div>
      <div style={{ fontSize: '12px', fontWeight: '500', letterSpacing: '0.3em', color: '#00c8a0', textTransform: 'uppercase', marginBottom: '48px' }}>Territorial Intelligence</div>
      <div style={{ fontSize: '18px', color: 'rgba(255,255,255,0.85)', fontWeight: '300', textAlign: 'center', maxWidth: '480px', lineHeight: '1.6', marginBottom: '16px', fontStyle: 'italic' }}>
        "El territorio habla.<br />VITA lo interpreta antes del conflicto."
      </div>
      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginTop: '48px', animation: 'pulse 1.5s ease-in-out infinite' }}>Toca cualquier parte para continuar</div>
    </div>
  );
}

function ClientApp() {
  const { currentUser, logout } = useAuth();
  const mainRef = useRef(null);
  const reportRef = useRef(null);

  const [showWelcome, setShowWelcome] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedOperationalItem, setSelectedOperationalItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState('');
  const [actors, setActors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [socialSignals, setSocialSignals] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [informeCliente, setInformeCliente] = useState(null);

  const [territorialFilters] = useState({
    selectedRegion: '', selectedComuna: '', selectedSector: '', selectedFoco: ''
  });

  useEffect(() => {
    const cargar = async () => {
      // 1. Leer qué proyecto tiene permitido este cliente
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      const permitidos = userData.proyectosPermitidos || [];

      // Sin proyecto permitido → no carga nada
      if (permitidos.length === 0) {
        setLoading(false);
        return;
      }

      // Cliente ve UN solo proyecto: el primero permitido
      const miProyecto = permitidos[0];

      // 2. Cargar SOLO los datos de ese proyecto (filtrado en el servidor)
      const [pSnap, aSnap, alSnap, tSnap, sSnap, snapSnap, infSnap] = await Promise.all([
        getDoc(doc(db, 'proyectos', miProyecto)),
        getDocs(query(collection(db, 'actores'), where('projectId', '==', miProyecto))),
        getDocs(query(collection(db, 'alertas'), where('projectId', '==', miProyecto))),
        getDocs(query(collection(db, 'timeline'), where('projectId', '==', miProyecto))),
        getDocs(query(collection(db, 'socialSignals'), where('projectId', '==', miProyecto))),
        getDocs(query(collection(db, 'snapshots'), where('projectId', '==', miProyecto))),
        getDocs(query(collection(db, 'informes'), where('projectId', '==', miProyecto)))
      ]);

      // Proyecto
      if (pSnap.exists()) {
        setProjects([{ id: pSnap.id, ...pSnap.data() }]);
        setActiveProjectId(pSnap.id);
      }

      setActors(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAlerts(alSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimelineEvents(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSocialSignals(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSnapshots(snapSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Informe más reciente del proyecto
      const informes = infSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      if (informes.length > 0) setInformeCliente(informes[0]);

      setLoading(false);
    };
    cargar();
  }, [currentUser.uid]);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeView]);

  const activeProject = projects.find(p => p.id === activeProjectId);
  const projectActors = actors.filter(a => a.projectId === activeProjectId);
  const projectAlerts = alerts.filter(a => a.projectId === activeProjectId);
  const projectTimeline = timelineEvents.filter(e => e.projectId === activeProjectId);
  const projectSignals = socialSignals.filter(s => s.projectId === activeProjectId);

  const { processedActors } = useVitaEngine(projectActors, projectAlerts, projectSignals);
  const cognitiveAlerts = useCognitiveAlerts(processedActors);
  const projectPrediction = buildProjectPrediction(snapshots, processedActors);

  const filteredAlerts = projectAlerts;
  const filteredSignals = projectSignals;

  const handleNavClick = (view) => {
    if (view === activeView) {
      mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setActiveView(view);
    }
  };

  const exportarPDFDirecto = () => {
    if (reportRef.current) reportRef.current.exportarPDF();
  };

  if (loading) {
    return (
      <div style={{ background: '#071a2f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'white', fontSize: '18px', fontWeight: '700' }}>Cargando VITA...</div>
      </div>
    );
  }

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <FiShield /> },
    { id: 'busqueda', label: 'Búsqueda', icon: <FiSearch /> },
    { id: 'analytics', label: 'Analytics', icon: <FiBarChart2 /> },
  ];

  return (
    <>
      {showWelcome && <WelcomeScreen onDismiss={() => setShowWelcome(false)} />}

      <div style={styles.app}>
        <div style={styles.sidebar}>
          <div style={styles.sidebarLogo}>
            <div style={styles.logoRow}>
              <img src={vitaIcon} alt="VITA" style={styles.logoIcon} />
              <div>
                <div style={styles.logoText}>VITA</div>
                <div style={styles.logoSub}>Territorial Intelligence</div>
              </div>
            </div>
          </div>

          <nav style={styles.nav}>
            {navItems.map(item => (
              <button key={item.id}
                style={{ ...styles.navItem, ...(activeView === item.id ? styles.navItemActive : {}) }}
                onClick={() => handleNavClick(item.id)}>
                <span style={styles.navIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <div style={styles.sidebarBottom}>
            <button style={styles.pdfButton} onClick={exportarPDFDirecto}>
              <FiMap /> Informe PDF
            </button>
            <button style={styles.logoutBtn} onClick={logout}>
              <FiLogOut /> Salir
            </button>
          </div>
        </div>

        <div ref={mainRef} style={styles.main}>
          {activeView === 'dashboard' && (
            <>
              <Dashboard
                totalActores={processedActors.length}
                actoresCriticos={processedActors.filter(a => ['Activación Territorial', 'Convergencia Narrativa', 'Escalamiento Territorial'].includes(a._vita?.state?.state)).length}
                alertasActivas={filteredAlerts.filter(a => a.estado !== 'Cerrada').length}
                totalEventos={filteredSignals.length}
                projectActors={processedActors}
                projectAlerts={filteredAlerts}
                projectSignals={filteredSignals}
                projectTimeline={projectTimeline}
                activeProject={activeProject}
                projects={projects}
                activeProjectId={activeProjectId}
                setActiveProjectId={null}
                logout={logout}
                onOpenReport={exportarPDFDirecto}
              />
              <MapSection
                projectActors={processedActors}
                projectAlerts={projectAlerts}
                projectSignals={projectSignals}
                projectTimeline={projectTimeline}
                mode="view"
                selectedEntity={selectedOperationalItem}
                territorialFilters={territorialFilters}
                setTerritorialFilters={() => {}}
                activeProject={activeProject}
              />
              <KnowledgeGraph processedActors={processedActors} />
              <CognitiveAlerts alerts={cognitiveAlerts} />
            </>
          )}

          {activeView === 'busqueda' && (
            <GlobalSearch
              projectActors={projectActors}
              projectAlerts={projectAlerts}
              projectSignals={projectSignals}
              projectTimeline={projectTimeline}
            />
          )}

          {activeView === 'analytics' && (
            <AnalyticsPanel
              projectActors={projectActors}
              projectAlerts={projectAlerts}
              projectSignals={projectSignals}
            />
          )}

          <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '1200px' }}>
            <ExecutiveReport
              ref={reportRef}
              activeProject={activeProject}
              activeProjectId={activeProjectId}
              projectActors={projectActors}
              processedActors={processedActors}
              projectAlerts={projectAlerts}
              projectTimeline={projectTimeline}
              projectSignals={projectSignals}
              territorialFilters={territorialFilters}
              projectPrediction={projectPrediction}
              informeExternoTexto={informeCliente?.texto || ''}
            />
          </div>
        </div>

        <TerritorialPanel
          selectedItem={selectedOperationalItem ? (processedActors.find(a => a.id === selectedOperationalItem.id) || selectedOperationalItem) : null}
          onClose={() => setSelectedOperationalItem(null)}
          projectActors={projectActors}
          projectAlerts={projectAlerts}
          projectSignals={projectSignals}
          projectTimeline={projectTimeline}
          onRelatedSelect={setSelectedOperationalItem}
        />
      </div>
    </>
  );
}

const styles = {
  app: { display: 'flex', fontFamily: 'Arial', background: '#071a2f', minHeight: '100vh' },
  sidebar: { width: '220px', minWidth: '220px', background: 'linear-gradient(180deg,#071a2f,#0b2442)', borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', padding: '24px 16px', height: '100vh', position: 'sticky', top: 0 },
  sidebarLogo: { marginBottom: '32px', paddingLeft: '8px' },
  logoText: { fontSize: '48px', fontWeight: '800', color: 'white', lineHeight: '1', marginTop: '2px', marginBottom: '-4px' },
  logoSub: { marginTop: '-4px', color: 'rgba(255,255,255,0.62)', fontSize: '13px' },
  nav: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '12px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.55)', fontSize: '14px', fontWeight: '600', cursor: 'pointer', textAlign: 'left', width: '100%' },
  navItemActive: { background: 'rgba(103,232,249,0.1)', color: '#67e8f9', border: '1px solid rgba(103,232,249,0.2)' },
  navIcon: { fontSize: '16px', display: 'flex', alignItems: 'center' },
  sidebarBottom: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' },
  pdfButton: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px', borderRadius: '12px', border: '1px solid rgba(103,232,249,0.25)', background: 'rgba(103,232,249,0.08)', color: '#67e8f9', fontSize: '13px', fontWeight: '700', cursor: 'pointer', width: '100%' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', width: '100%' },
  logoRow: { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '32px', marginTop: '-8px' },
  logoIcon: { width: '92px', height: '92px', objectFit: 'contain', marginLeft: '-8px', marginRight: '-10px' },
  main: { flex: 1, padding: '32px 42px 48px', background: '#071a2f', overflowX: 'hidden', overflowY: 'auto', height: '100vh' }
};

export default ClientApp;